import { useMemo } from 'react';
import Grid from '@mui/material/Unstable_Grid2';

import { useLocales } from 'src/locales';

import FunnelKpiCard from './FunnelKpiCard';

interface FunnelKpiCardsProps {
  kpisData: any;
  compareData?: any;
  kpisError: any;
  isFetchingKpis: boolean;
  workspace: any;
}

export default function FunnelKpiCards({ kpisData, compareData, kpisError, isFetchingKpis, workspace }: FunnelKpiCardsProps) {
  const { t } = useLocales();

  const getRevenueOrConversionsTexts = (ws: any) => {
    const isLead = ws?.template === 'Lead';
    const isEcommerce = ws?.template === 'E-commerce';

    const hasGA4 =
      Array.isArray(ws?.conversions_google_analytics) &&
      ws.conversions_google_analytics.length > 0;

    const hasAds =
      Array.isArray(ws?.conversions_google_ads) &&
      ws.conversions_google_ads.length > 0;

    const isCustom = ws?.custom === true;

    if (isLead) {
      if (isCustom) {
        return {
          label: t('dashboard.global.custom_based_conversions'),
          definition: t('definitions.custom_based_conversions'),
        };
      }
      if (hasGA4) {
        return {
          label: t('dashboard.global.total_search_conversions'),
          definition: t('definitions.total_search_conversions'),
        };
      }
      if (hasAds) {
        return {
          label: t('dashboard.global.brand_conversions_gads'),
          definition: t('definitions.brand_conversions_gads'),
        };
      }
    }

    if (isEcommerce) {
      if (isCustom) {
        return {
          label: t('dashboard.global.custom_based_revenue'),
          definition: t('definitions.custom_based_revenue'),
        };
      }
      if (hasGA4) {
        return {
          label: t('dashboard.global.total_search_revenue'),
          definition: t('definitions.total_search_revenue'),
          isCurrency: true,
        };
      }
      if (hasAds) {
        return {
          label: t('dashboard.global.brand_revenue_gads'),
          definition: t('definitions.brand_revenue_gads'),
        };
      }
    }

    return { label: 'N/A', definition: 'N/A' };
  };

  const revenueOrConversionsTexts = useMemo(
    () => getRevenueOrConversionsTexts(workspace),
    [workspace, t]
  );

  const stats = useMemo(
    () => [
      {
        title: t('dashboard.brandTraffic.brandSearches'),
        value: kpisData?.organicSearches ?? 0,
        compare: compareData?.organicSearches,
        definition: t('definitions.brand_searches'),
      },
      {
        title: t('dashboard.brandTraffic.totalBrandTraffic'),
        value: kpisData?.brandTraffic ?? 0,
        compare: compareData?.brandTraffic,
        definition: t('definitions.brand_traffic'),
      },
      {
        title: t('dashboard.brandTraffic.blendedCtr'),
        value: kpisData?.blendedCtr ?? 0,
        compare: compareData?.blendedCtr,
        isPercent: true,
        definition: t('definitions.blended_ctr'),
      },
      {
        title: revenueOrConversionsTexts.label,
        value: kpisData?.revenue_or_conversions ?? 0,
        compare: compareData?.revenue_or_conversions,
        isCurrency: revenueOrConversionsTexts?.isCurrency,
        definition: revenueOrConversionsTexts.definition,
      },
      {
        title: t('dashboard.global.ad_cost'),
        value: kpisData?.adCost ?? 0,
        compare: compareData?.adCost,
        isCurrency: true,
        invertedColor: true,
        definition: t('definitions.ad_cost'),
      },
    ],
    [kpisData, compareData, t, revenueOrConversionsTexts]
  );

  const isFetching = isFetchingKpis || (!kpisData && !kpisError);

  return (
    <Grid container spacing={1.5}>
      {stats.map((stat, index) => (
        <Grid key={index} xs={6} md={4} lg={2.4}>
          <FunnelKpiCard
            title={stat.title}
            total={stat.value}
            compare={stat.compare}
            definition={stat.definition}
            isPercent={stat.isPercent}
            isCurrency={stat.isCurrency}
            invertedColor={stat.invertedColor}
            isFetching={isFetching}
          />
        </Grid>
      ))}
    </Grid>
  );
}
